import { Container } from '@playcanvas/pcui';

const MIN_WIDTH = 260;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 320;
const KEYBOARD_STEP = 16;
const STORAGE_KEY = 'simam.leftColumnWidth';

const clampWidth = (width: number) => Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width)));

// Storage can be unavailable - private windows, blocked site data - and the accessor itself
// can throw. The width is a convenience, so any failure just means the default.
const loadWidth = () => {
    try {
        const stored = Number(window.localStorage.getItem(STORAGE_KEY));
        return stored ? clampWidth(stored) : DEFAULT_WIDTH;
    } catch {
        return DEFAULT_WIDTH;
    }
};

const saveWidth = (width: number) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
        // losing the remembered width is harmless
    }
};

// The docked column to the left of the viewport, holding the scene panel and the Properties
// panel. Its width is user-resizable and remembered per browser.
class LeftColumn extends Container {
    // Mounted by the editor as the column's next sibling rather than inside it: the column
    // scrolls, and a handle inside it would scroll away with the content.
    resizeHandle: HTMLElement;

    constructor(args = {}) {
        args = {
            ...args,
            id: 'left-column'
        };

        super(args);

        const handle = document.createElement('div');
        handle.id = 'left-column-resize';
        handle.tabIndex = 0;
        handle.setAttribute('role', 'separator');
        handle.setAttribute('aria-orientation', 'vertical');
        handle.setAttribute('aria-valuemin', String(MIN_WIDTH));
        handle.setAttribute('aria-valuemax', String(MAX_WIDTH));
        this.resizeHandle = handle;

        const setWidth = (width: number) => {
            const clamped = clampWidth(width);
            this.dom.style.width = `${clamped}px`;
            handle.setAttribute('aria-valuenow', String(clamped));
            return clamped;
        };

        // box-sizing is border-box (style.scss), so the rendered width equals the style width
        const currentWidth = () => this.dom.getBoundingClientRect().width;

        setWidth(loadWidth());

        handle.addEventListener('pointerdown', (event: PointerEvent) => {
            if (event.button !== 0) {
                return;
            }

            event.preventDefault();
            handle.setPointerCapture(event.pointerId);
            handle.classList.add('dragging');

            const startX = event.clientX;
            const startWidth = currentWidth();

            const move = (moveEvent: PointerEvent) => {
                setWidth(startWidth + moveEvent.clientX - startX);
            };

            // releasing the pointer ends capture, so this covers pointerup and cancellation
            const end = () => {
                handle.classList.remove('dragging');
                handle.removeEventListener('pointermove', move);
                handle.removeEventListener('lostpointercapture', end);
                saveWidth(setWidth(currentWidth()));
            };

            handle.addEventListener('pointermove', move);
            handle.addEventListener('lostpointercapture', end);
        });

        handle.addEventListener('keydown', (event: KeyboardEvent) => {
            let step = 0;
            if (event.key === 'ArrowRight') {
                step = KEYBOARD_STEP;
            } else if (event.key === 'ArrowLeft') {
                step = -KEYBOARD_STEP;
            }

            if (step) {
                event.preventDefault();
                event.stopPropagation();
                saveWidth(setWidth(currentWidth() + step));
            }
        });
    }
}

export { LeftColumn };
